/**
 * Server-safe structured-data (JSON-LD) builders.
 *
 * Вынесено в отдельный модуль БЕЗ зависимостей (в отличие от lib/seo.js, который
 * транзитивно тянет JSX-в-.js хелперы) — чтобы функции были чисто тестируемыми и
 * импортируемыми и на сервере, и в клиентских компонентах.
 *
 * Заменяет 6 ранее инлайненных реализаций FAQPage JSON-LD с 4 расходившимися
 * стратегиями очистки ответа.
 */

/**
 * Serialize structured data for embedding in an HTML <script> element.
 * JSON.stringify alone is unsafe here because a CMS value containing
 * `</script>` can terminate the element before the JSON parser sees it.
 */
export function serializeJsonLd(value, space) {
    return JSON.stringify(value, null, space)
        .replace(/</g, '\\u003c')
        .replace(/\u2028/g, '\\u2028')
        .replace(/\u2029/g, '\\u2029');
}

/**
 * Единая очистка текста ответа FAQ для structured data.
 *   - PortableText-массив → флэттенит ВСЕ блоки (раньше countryPage/citiesPage
 *     брали только answer[0] и теряли последующие абзацы);
 *   - строка → как есть;
 *   - прочее → String();
 *   - всегда: убирает HTML-теги и разворачивает declension-плейсхолдеры
 *     вида `{Черногория[acc]}` → `Черногория` (no-op если их нет).
 * @param {string|Array|*} raw
 * @returns {string}
 */
export function buildFaqAnswerText(raw) {
    if (raw == null) return '';
    let text;
    if (Array.isArray(raw)) {
        text = raw
            .map((block) => {
                if (typeof block === 'string') return block;
                if (block && Array.isArray(block.children)) {
                    return block.children.map((c) => c?.text || '').join('');
                }
                return '';
            })
            .join('\n');
    } else if (typeof raw === 'string') {
        text = raw;
    } else {
        text = String(raw);
    }
    return text
        .replace(/<[^>]*>/g, '') // strip HTML
        .replace(/\{([^[]+)\[[A-Za-z][A-Za-z0-9_-]*\]\}/g, '$1') // unwrap declension placeholders
        .trim();
}

/**
 * Собирает объект FAQPage JSON-LD. Поддерживает оба shape элемента:
 * `{ question, answer }` и `{ q, a }`.
 *
 * @param {Array} faqs
 * @param {object} [opts]
 * @param {string} [opts.pageUrl] — если задан, добавляет `@id = ${pageUrl}#faq`
 * @param {string} [opts.id] — явный `@id` (приоритетнее pageUrl)
 * @param {string} [opts.lang] — 'ru'|'en' → inLanguage
 * @param {number} [opts.limit=Infinity] — ограничение числа вопросов
 * @returns {object|null} объект JSON-LD или null, если FAQ пуст
 */
/**
 * Собирает BreadcrumbList JSON-LD из списка крошек.
 * @param {Array<{name: string, item: string}>} crumbs — по порядку, от корня к листу
 * @returns {object|null}
 */
export function buildBreadcrumbJsonLd(crumbs) {
    const items = (Array.isArray(crumbs) ? crumbs : []).filter((c) => c && c.name && c.item);
    if (items.length === 0) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            item: c.item,
        })),
    };
}

export function buildFaqJsonLd(faqs, { pageUrl, id, lang, limit = Infinity } = {}) {
    const items = Array.isArray(faqs) ? faqs.filter(Boolean) : [];
    const sliced = Number.isFinite(limit) ? items.slice(0, limit) : items;
    if (sliced.length === 0) return null;
    const faqId = id || (pageUrl ? `${pageUrl}#faq` : undefined);
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        ...(faqId ? { '@id': faqId } : {}),
        ...(lang ? { inLanguage: lang === 'ru' ? 'ru-RU' : 'en-US' } : {}),
        mainEntity: sliced.map((item) => ({
            '@type': 'Question',
            name: item.question ?? item.q ?? '',
            acceptedAnswer: {
                '@type': 'Answer',
                text: buildFaqAnswerText(item.answer ?? item.a ?? ''),
            },
        })),
    };
}
